import {
    Box,
    Button,
    Flex,
    IconButton,
    Input,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Stack,
    Text,
    VStack,
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CTable from "../../../components/Table/table";
import Select from "react-select";
import { attendanceReportRequest } from "../../../modules/admin-modules/reports/Actions";
import { getAllLecturesRequest } from "../../../modules/admin-modules/lectures/Actions";
import Layout from "../../../components/dashboard/Layout";
import LogoutMenu from "../../../components/dashboard/Layout/logout-menu";
import dayjs from "dayjs";
import { CSVLink } from "react-csv";
import { getManualExamsRequest } from "../../../modules/admin-modules/exams/Actions";
import { getManualExamsMarksRequest } from "../../../modules/admin-modules/exams/Actions";
import { setManualExamMarkRequest } from "../../../modules/admin-modules/exams/Actions";

const getExamMaxScore = (exam) => {
    if (exam?.no_of_questions != null && exam.no_of_questions !== "") {
        const direct = Number(exam.no_of_questions);
        if (Number.isFinite(direct) && direct >= 0) return direct;
    }

    if (Array.isArray(exam?.chapters) && exam.chapters.length) {
        const total = exam.chapters.reduce((sum, chapter) => {
            const count = Number(
                chapter?.no_of_questions ?? chapter?.pivot?.no_of_questions ?? 0
            );
            return sum + (Number.isFinite(count) ? count : 0);
        }, 0);
        if (total > 0) return total;
    }

    const questionNumber = Number(exam?.question_number);
    if (Number.isFinite(questionNumber) && questionNumber > 0) return questionNumber;

    return null;
};

const clampScore = (raw, maxScore) => {
    if (raw === "" || raw == null) return "";
    const value = Number(raw);
    if (!Number.isFinite(value)) return "";
    if (value < 0) return "0";
    if (maxScore != null && value > maxScore) return String(maxScore);
    return String(raw);
};

const AttendanceSimpleReport = ({ setAdd, setSelectedData }) => {
    const dispatch = useDispatch();
    const [lecId, setLecId] = useState("");
    const [date, setDate] = useState(dayjs(new Date()).format("YYYY-MM-DD"));
    const [examId, setExamId] = useState("");
    const [selectedExam, setSelectedExam] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [sendStatus, setSendStatus] = useState({});

    const user = useSelector((state) => state.user.userData);

    const [marks, setMarks] = useState([]); // Add state for marks
    const [scores, setScores] = useState({}); // Replace single score state with scores object

    // Get marks from Redux store
    const examMarks = useSelector((state) => state.exams.marks);

    // Watch for marks changes
    useEffect(() => {
        if (examMarks && examMarks.length > 0) {
            setMarks(examMarks);
        }
    }, [examMarks]);

    useEffect(() => {
        if (lecId) dispatch(attendanceReportRequest(lecId, date));
    }, [lecId, date]);

    useEffect(() => {
        dispatch(getAllLecturesRequest());
        dispatch(getManualExamsRequest());
    }, []);

    const maxScore = getExamMaxScore(selectedExam);

    const getMarks = (exam) => {
        const id = exam?.id || "";
        setExamId(id);
        setSelectedExam(exam || null);
        setScores({});
        setSendStatus({});
        if (id) dispatch(getManualExamsMarksRequest(id));
    };

    const sendGrade = (studentId, score) => {
        if (sendStatus[studentId] === "loading") return;
        const cappedScore = clampScore(score, maxScore);
        if (cappedScore === "") return;

        setSendStatus((prev) => ({ ...prev, [studentId]: "loading" }));
        dispatch(
            setManualExamMarkRequest(
                {
                    student_id: studentId,
                    exam_id: examId,
                    score: cappedScore,
                },
                (ok) => {
                    setSendStatus((prev) => ({
                        ...prev,
                        [studentId]: ok ? "success" : "idle",
                    }));
                }
            )
        );
    };

    const lectures = useSelector((state) => state.lectures.lectures);
    const exams = useSelector((state) => state.exams.manualExams);

    const lecOptions = lectures?.map((data) => {
        return { value: data.id, label: data.title };
    });

    const attendancesReport = useSelector(
        (state) => state.reports.attendancesReport
    );

    const [offset, setPage] = useState(0);

    const [size, setPerPage] = useState(10);
    const [pageNumber, setPageNumber] = useState(0);
    const [selected, setSelected] = useState({
        id: "",
    });
    const colourStyles = {
        control: (styles) => ({
            ...styles,
            width: "100%",
            background: "#f5f5f5",
            color: "#690000",
            borderRadius: "16px",
        }),
        option: (styles) => ({ ...styles, color: "#690000" }),
        menu: (styles) => ({ ...styles, zIndex: 9999 }),
        menuList: (styles) => ({ ...styles, zIndex: 9999 }),
    };

    const viewData = (data) => {
        setSelectedData(data);
        setSelected(data);
    };

    const headers = [
        { label: "name", key: "name" },
        { label: "phone", key: "phone" },
        // Only include grade and send_grade headers if an exam is selected
        ...(examId ? [
            { label: "grade", key: "grade" },
            { label: "send_grade", key: "send_grade" }
        ] : [])
    ];

    const Data = attendancesReport?.map((data, index) => {
        const studentMarks = marks.find((mark) => mark.student_id === data?.id);
        const baseData = {
            ...data,
            id: data?.id,
            name: data?.name,
            phone: data?.phone,
            user_name: data?.user_name,
        };
        
        // Only add grade and send_grade fields if an exam is selected
        if (examId) {
            const enteredScore = scores[data.id];
            const currentScore =
                enteredScore !== undefined ? enteredScore : studentMarks?.score ?? "";
            const status = sendStatus[data.id];
            const hasScore = currentScore !== "" && currentScore != null;

            baseData.grade = (
                <Input
                    type="number"
                    min={0}
                    max={maxScore ?? undefined}
                    step="any"
                    placeholder={maxScore != null ? `0 - ${maxScore}` : "Enter Grade"}
                    _placeholder={{ color: "black" }}
                    value={currentScore}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => {
                        const nextScore = clampScore(e.target.value, maxScore);
                        setScores((prev) => ({
                            ...prev,
                            [data.id]: nextScore,
                        }));
                        if (status === "success") {
                            setSendStatus((prev) => ({
                                ...prev,
                                [data.id]: "idle",
                            }));
                        }
                    }}
                    style={{
                        backgroundColor:
                            !studentMarks && !scores[data.id] ? "#FF7F7F" : "#90EE90",
                    }}
                />
            );
            baseData.send_grade = (
                <Button
                    minW="120px"
                    isLoading={status === "loading"}
                    loadingText="Sending"
                    isDisabled={!hasScore || status === "loading"}
                    colorScheme={status === "success" ? "green" : undefined}
                    onClick={(e) => {
                        e.stopPropagation();
                        sendGrade(data?.id, currentScore);
                    }}
                >
                    {status === "success" ? "✔" : "Send Grade"}
                </Button>
            );
        }
        
        return baseData;
    });

    // Filter data based on search term
    const filteredData = Data?.filter((item) => {
        if (!searchTerm) return true;
        const searchLower = searchTerm.toLowerCase();
        return (
            item.name?.toLowerCase().includes(searchLower) ||
            item.phone?.toLowerCase().includes(searchLower) ||
            item.user_name?.toLowerCase().includes(searchLower)
        );
    });

    const columns = [
        {
            Header: "اسم الطالب",
            accessor: "name",
        },
        {
            Header: "كود الطالب",
            accessor: "user_name",
        },
        // Only include grade and send_grade columns if an exam is selected
        ...(examId ? [
            {
                Header: "الدرجة",
                accessor: "grade",
            },
            {
                Header: "ارسال الدرجة",
                accessor: "send_grade",
            }
        ] : [])
    ];

    return (
        <Layout>
            <div style={{ minHeight: "400px", marginTop: "100px" }}>
                <Text fontSize="24px" fontWeight="600" color="main_1">
                    رصد درجات السنتر
                </Text>
                <VStack spacing={4} w="100%" my="30px">
                    <Flex w="100%" justifyContent="space-between" alignItems="center" style={{ position: 'relative', zIndex: 1000 }}>
                        <Select
                            options={lecOptions}
                            isMulti={false}
                            onChange={(data) => {
                                setLecId(data?.value);
                            }}
                            styles={colourStyles}
                            placeholder="Select lecture"
                            
                        />
                        <Input
                            placeholder="Select Date and Time"
                            size="md"
                            type="datetime-local"
                            w="50%"
                            mx="8px"
                            onChange={(e) => {
                                const formatedDate = dayjs(e.target.value).format("YYYY-MM-DD");
                                setDate(formatedDate);
                            }}
                        />
                        {filteredData?.length !== 0 && (
                            <Select
                                options={exams.map((data) => {
                                    return { value: data.id, label: data.name };
                                })}
                                isMulti={false}
                                onChange={(option) => {
                                    const exam = exams?.find(
                                        (item) => item.id === option?.value
                                    );
                                    getMarks(exam);
                                }}
                                styles={colourStyles}
                                placeholder="Select Exam"
                            />
                        )}
                    </Flex>
                    
                    <Flex w="100%" justifyContent="flex-start" alignItems="center">
                        <Input
                            placeholder="Search by name or phone..."
                            size="md"
                            w="300px"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </Flex>
                </VStack>

                <CTable
                    selectedData={viewData}
                    Columns={columns}
                    Data={filteredData}
                    path={"openModel"}
                    Actions
                    Title={``}
                    subTitle=""
                    btnTitle=""
                    placeHolder=""
                    noSearchBar={true}
                    noFilter={true}
                    footerBtnTitle
                    filterList
                    addButton
                    addButtonTitle=""
                    setPage={setPage}
                    setPerPage={setPerPage}
                    currentpage={pageNumber}
                    setPageNumber={setPageNumber}
                    noSecondSearchBar={true}
                    isLoading={false}
                    perPage={size}
                    totalPage={Math.ceil(10 / 50)}
                />
            </div>
        </Layout>
    );
};

export default AttendanceSimpleReport;
